import React, { useRef, useState } from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';

// Página a la que Stripe devuelve al cliente después de pagar la foto de $12
// (?s=<checkout session id>). El cliente sube una foto (cámara o galería), la
// función photo-process la valida con Snap2Pass y le envía por correo la foto
// 2x2 y la hoja 4x6. Todo automático.
// Si la foto no pasa, photo-process devuelve `reasons` (claves como 'lighting',
// 'background'...) y aquí se muestran consejos concretos en el idioma del cliente.
const ENDPOINT = 'https://jcbxwtuhshcyzlhpikxr.supabase.co/functions/v1/photo-process';
const ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpjYnh3dHVoc2hjeXpsaHBpa3hyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTA0NDc5MzAsImV4cCI6MjEwNjAyMzkzMH0.TCCTiwKNauedwdX3R0_Ag0MCZId2nZGmuLIN86FcH-U';
const CONTACT_EMAIL = 'info@noorpassportservices.com';
type Strings = {
  title: string;
  intro: string;
  tipsTitle: string;
  tips: string[];
  choose: string;
  sending: string;
  waitNote: string;
  okTitle: string;
  okBody: string;
  retryTitle: string;
  retryBody: string;
  reasonsTitle: string;
  reasons: Record<string, string>;
  left: string;
  exhaustedTitle: string;
  exhaustedBody: string;
  doneTitle: string;
  doneBody: string;
  missingTitle: string;
  missingBody: string;
  notFound: string;
  tooBig: string;
  generic: string;
  disclaimer: string;
};
const T: Record<Language, Strings> = {
  es: {
    title: 'Suba su foto de pasaporte',
    intro: 'Tome una selfie con su teléfono o elija una foto reciente de su galería. La validamos automáticamente y le enviamos por correo su foto 2x2 y la hoja 4x6 lista para imprimir.',
    tipsTitle: 'Para mejores resultados',
    tips: ['Mire de frente a la cámara, con expresión neutral y la boca cerrada.', 'Fondo claro y liso, sin sombras detrás de usted.', 'Luz pareja en el rostro. Sin lentes, gorra ni sombrero.', 'Cámara a la altura de los ojos; muestre cabeza y hombros.'],
    choose: 'Tomar o elegir foto',
    sending: 'Procesando su foto…',
    waitNote: 'Esto toma unos 20 segundos. No cierre esta página.',
    okTitle: '¡Listo! Su foto fue validada',
    okBody: 'Le enviamos por correo su foto 2x2 y la hoja 4x6. Revise su bandeja de entrada (y la carpeta de correo no deseado).',
    retryTitle: 'Esta foto no pasó la validación',
    retryBody: 'No se preocupe, puede intentarlo de nuevo. Siga los consejos de arriba y tome la foto otra vez.',
    reasonsTitle: 'Qué conviene corregir:',
    reasons: {
      no_face: 'No logramos ver su rostro. Mire de frente a la cámara y muestre toda la cara, bien iluminada.',
      eyes: 'Sus ojos deben verse abiertos y completamente visibles. Mire directamente a la cámara.',
      mouth: 'Mantenga la boca cerrada y una expresión neutral, sin sonreír.',
      glasses: 'Quítese los lentes; no se permiten en la foto de pasaporte.',
      hat: 'Quítese la gorra, el sombrero o cualquier cosa que cubra la cabeza.',
      background: 'El fondo debe ser claro y liso, sin sombras ni objetos detrás de usted. Párese frente a una pared blanca o clara.',
      lighting: 'La luz debe ser pareja en el rostro, sin sombras ni reflejos. Párese de frente a una ventana, con la luz del día.',
      centered: 'Centre su rostro y muestre cabeza y hombros, ni muy cerca ni muy lejos de la cámara.',
      straight: 'Mantenga la cabeza derecha y mirando de frente, sin inclinarla ni girarla.',
      blur: 'La foto salió borrosa. Sostenga el teléfono firme (o apóyelo en algo) y use buena luz.',
      resolution: 'La imagen tiene poca calidad. Use la cámara del teléfono; evite capturas de pantalla o fotos reenviadas.',
      format: 'No pudimos leer el archivo. Use una foto JPG o PNG tomada con su teléfono.'
    },
    left: 'Intentos restantes:',
    exhaustedTitle: 'Usó todos sus intentos',
    exhaustedBody: 'Ya recibimos el aviso y nos comunicaremos con usted por correo para ayudarle.',
    doneTitle: 'Su foto ya fue enviada',
    doneBody: 'Revise su correo: ahí están su foto 2x2 y la hoja 4x6.',
    missingTitle: 'Falta el enlace de su pago',
    missingBody: 'Abra esta página desde el enlace que le enviamos por correo después de pagar. Si necesita ayuda, escríbanos a',
    notFound: 'No encontramos su pago. Si ya pagó, escríbanos a',
    tooBig: 'La foto es demasiado grande. Intente con otra.',
    generic: 'Ocurrió un problema. Intente de nuevo o escríbanos a',
    disclaimer: 'Validamos su foto con una herramienta automática y no alteramos su imagen. La aceptación final corresponde al Departamento de Estado.'
  },
  en: {
    title: 'Upload your passport photo',
    intro: 'Take a selfie with your phone or choose a recent photo from your gallery. We validate it automatically and email you your 2x2 photo and a print-ready 4x6 sheet.',
    tipsTitle: 'For best results',
    tips: ['Face the camera directly with a neutral expression and mouth closed.', 'Plain, light background with no shadows behind you.', 'Even lighting on your face. No glasses, cap or hat.', 'Camera at eye level; show your head and shoulders.'],
    choose: 'Take or choose photo',
    sending: 'Processing your photo…',
    waitNote: 'This takes about 20 seconds. Please keep this page open.',
    okTitle: 'Done! Your photo was validated',
    okBody: 'We emailed you your 2x2 photo and the 4x6 sheet. Check your inbox (and your spam folder).',
    retryTitle: 'This photo did not pass validation',
    retryBody: 'No worries, you can try again. Follow the tips above and take the photo once more.',
    reasonsTitle: 'What to fix:',
    reasons: {
      no_face: 'We could not see your face. Look straight at the camera and show your whole face, well lit.',
      eyes: 'Your eyes must be open and fully visible. Look directly at the camera.',
      mouth: 'Keep your mouth closed with a neutral expression, no smiling.',
      glasses: 'Take off your glasses; they are not allowed in a passport photo.',
      hat: 'Remove your cap, hat or anything covering your head.',
      background: 'The background must be plain and light, with no shadows or objects behind you. Stand in front of a white or light wall.',
      lighting: 'Light must be even on your face, with no shadows or glare. Face a window with daylight.',
      centered: 'Center your face and show head and shoulders, not too close or too far from the camera.',
      straight: 'Keep your head straight and facing forward, without tilting or turning.',
      blur: 'The photo came out blurry. Hold the phone steady (or rest it on something) and use good light.',
      resolution: 'The image quality is low. Use your phone camera; avoid screenshots or forwarded photos.',
      format: 'We could not read the file. Use a JPG or PNG photo taken with your phone.'
    },
    left: 'Attempts left:',
    exhaustedTitle: 'You have used all your attempts',
    exhaustedBody: 'We have been notified and will contact you by email to help.',
    doneTitle: 'Your photo was already sent',
    doneBody: 'Check your email: your 2x2 photo and 4x6 sheet are there.',
    missingTitle: 'Your payment link is missing',
    missingBody: 'Open this page from the link we emailed you after payment. If you need help, write to',
    notFound: 'We could not find your payment. If you already paid, write to',
    tooBig: 'The photo is too large. Please try another one.',
    generic: 'Something went wrong. Try again or write to',
    disclaimer: 'We validate your photo with an automated tool and do not alter your image. Final acceptance is up to the Department of State.'
  },
  ht: {
    title: 'Voye foto paspò ou',
    intro: 'Pran yon selfi ak telefòn ou oswa chwazi yon foto resan nan galri ou. Nou valide l otomatikman epi nou voye foto 2x2 ou a ak fèy 4x6 a pa imèl.',
    tipsTitle: 'Pou pi bon rezilta',
    tips: ['Gade kamera a direkteman, san emosyon, bouch fèmen.', 'Fon klè ak plat, pa gen lonbraj dèyè ou.', 'Limyè egal sou figi ou. Pa gen linèt, kasket oswa chapo.', 'Kamera a nivo je ou; montre tèt ak zepòl ou.'],
    choose: 'Pran oswa chwazi foto',
    sending: 'Nap trete foto ou…',
    waitNote: 'Sa pran apeprè 20 segonn. Pa fèmen paj sa a.',
    okTitle: 'Fini! Foto ou valide',
    okBody: 'Nou voye foto 2x2 ou a ak fèy 4x6 a pa imèl. Gade nan bwat resepsyon ou (ak nan spam).',
    retryTitle: 'Foto sa a pa pase validasyon',
    retryBody: 'Pa enkyete w, ou ka eseye ankò. Swiv konsèy yo anlè a epi pran foto a yon lòt fwa.',
    reasonsTitle: 'Sa pou korije:',
    reasons: {
      no_face: 'Nou pa t kapab wè figi ou. Gade kamera a dirèkteman epi montre tout figi ou, byen limine.',
      eyes: 'Je ou dwe louvri epi totalman vizib. Gade kamera a dirèkteman.',
      mouth: 'Kenbe bouch ou fèmen, san emosyon, pa souri.',
      glasses: 'Wete linèt ou; yo pa pèmèt yo nan foto paspò.',
      hat: 'Wete kasket, chapo oswa nenpòt bagay ki kouvri tèt ou.',
      background: 'Fon an dwe klè ak plat, san lonbraj ni objè dèyè ou. Kanpe devan yon miray blan oswa klè.',
      lighting: 'Limyè a dwe egal sou figi ou, san lonbraj ni reflè. Kanpe fas ak yon fenèt ak limyè jou.',
      centered: 'Mete figi ou nan mitan epi montre tèt ak zepòl ou, pa two pre ni two lwen kamera a.',
      straight: 'Kenbe tèt ou dwat epi gade devan, san panche l ni vire l.',
      blur: 'Foto a soti twoub. Kenbe telefòn nan fèm (oswa apiye l sou yon bagay) epi sèvi ak bon limyè.',
      resolution: 'Kalite imaj la ba. Sèvi ak kamera telefòn ou; evite kapti ekran oswa foto yo voye ba ou.',
      format: 'Nou pa t kapab li fichye a. Sèvi ak yon foto JPG oswa PNG ou pran ak telefòn ou.'
    },
    left: 'Esè ki rete:',
    exhaustedTitle: 'Ou itilize tout esè ou yo',
    exhaustedBody: 'Nou resevwa avi a epi nou pral kontakte ou pa imèl pou ede ou.',
    doneTitle: 'Nou deja voye foto ou',
    doneBody: 'Gade imèl ou: foto 2x2 ou a ak fèy 4x6 a la.',
    missingTitle: 'Lyen peman ou manke',
    missingBody: 'Louvri paj sa a ak lyen nou voye ba ou pa imèl apre peman an. Si w bezwen èd, ekri nou nan',
    notFound: 'Nou pa jwenn peman ou. Si ou deja peye, ekri nou nan',
    tooBig: 'Foto a two gwo. Eseye yon lòt.',
    generic: 'Gen yon pwoblèm. Eseye ankò oswa ekri nou nan',
    disclaimer: 'Nou valide foto ou ak yon zouti otomatik epi nou pa chanje imaj ou. Se Depatman Deta ki deside akseptasyon final la.'
  },
  vi: {
    title: 'Tải ảnh hộ chiếu của quý vị',
    intro: 'Chụp ảnh tự sướng bằng điện thoại hoặc chọn một ảnh gần đây trong thư viện. Chúng tôi tự động kiểm tra và gửi email ảnh 2x2 cùng tờ 4x6 sẵn sàng để in.',
    tipsTitle: 'Để có kết quả tốt nhất',
    tips: ['Nhìn thẳng vào máy ảnh, vẻ mặt trung tính, ngậm miệng.', 'Nền sáng, trơn, không có bóng phía sau.', 'Ánh sáng đều trên khuôn mặt. Không đeo kính, mũ.', 'Máy ảnh ngang tầm mắt; thấy đầu và vai.'],
    choose: 'Chụp hoặc chọn ảnh',
    sending: 'Đang xử lý ảnh của quý vị…',
    waitNote: 'Việc này mất khoảng 20 giây. Vui lòng giữ trang này mở.',
    okTitle: 'Xong! Ảnh của quý vị đã được xác nhận',
    okBody: 'Chúng tôi đã gửi email ảnh 2x2 và tờ 4x6. Hãy kiểm tra hộp thư đến (và thư rác).',
    retryTitle: 'Ảnh này chưa đạt yêu cầu',
    retryBody: 'Quý vị đừng lo, có thể thử lại. Hãy làm theo các lưu ý ở trên và chụp lại ảnh.',
    reasonsTitle: 'Cần chỉnh lại:',
    reasons: {
      no_face: 'Chúng tôi không thấy khuôn mặt của quý vị. Hãy nhìn thẳng vào máy ảnh và để lộ toàn bộ khuôn mặt, đủ sáng.',
      eyes: 'Mắt phải mở và nhìn thấy rõ hoàn toàn. Hãy nhìn thẳng vào máy ảnh.',
      mouth: 'Ngậm miệng và giữ vẻ mặt trung tính, không cười.',
      glasses: 'Hãy tháo kính; ảnh hộ chiếu không cho phép đeo kính.',
      hat: 'Hãy bỏ mũ hoặc bất cứ thứ gì che đầu.',
      background: 'Nền phải sáng và trơn, không có bóng hay vật phía sau. Hãy đứng trước bức tường trắng hoặc sáng màu.',
      lighting: 'Ánh sáng phải đều trên khuôn mặt, không có bóng hay chói. Hãy đứng đối diện cửa sổ có ánh sáng ban ngày.',
      centered: 'Đặt khuôn mặt ở giữa và thấy đầu cùng vai, không quá gần cũng không quá xa máy ảnh.',
      straight: 'Giữ đầu thẳng và nhìn về phía trước, không nghiêng hay quay.',
      blur: 'Ảnh bị mờ. Hãy cầm điện thoại thật vững (hoặc tựa vào vật gì đó) và dùng ánh sáng tốt.',
      resolution: 'Chất lượng ảnh thấp. Hãy dùng máy ảnh điện thoại; tránh ảnh chụp màn hình hoặc ảnh được chuyển tiếp.',
      format: 'Chúng tôi không đọc được tệp. Hãy dùng ảnh JPG hoặc PNG chụp bằng điện thoại.'
    },
    left: 'Số lần còn lại:',
    exhaustedTitle: 'Quý vị đã dùng hết số lần thử',
    exhaustedBody: 'Chúng tôi đã nhận được thông báo và sẽ liên hệ qua email để hỗ trợ.',
    doneTitle: 'Ảnh của quý vị đã được gửi',
    doneBody: 'Hãy kiểm tra email: ảnh 2x2 và tờ 4x6 ở đó.',
    missingTitle: 'Thiếu liên kết thanh toán',
    missingBody: 'Hãy mở trang này từ liên kết chúng tôi đã gửi qua email sau khi thanh toán. Nếu cần hỗ trợ, hãy viết cho chúng tôi tại',
    notFound: 'Chúng tôi không tìm thấy thanh toán của quý vị. Nếu đã thanh toán, hãy viết cho chúng tôi tại',
    tooBig: 'Ảnh quá lớn. Vui lòng thử ảnh khác.',
    generic: 'Đã xảy ra sự cố. Hãy thử lại hoặc viết cho chúng tôi tại',
    disclaimer: 'Chúng tôi kiểm tra ảnh bằng công cụ tự động và không chỉnh sửa hình ảnh. Việc chấp nhận cuối cùng thuộc về Bộ Ngoại giao.'
  }
};
type Status = 'idle' | 'sending' | 'ok' | 'retry' | 'exhausted' | 'done' | 'notfound' | 'error' | 'toobig';

// Reduce the picture to at most 1600px on its longest side as a JPEG.
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const max = 1600;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('canvas'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.9).split(',')[1]);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('img'));
    };
    img.src = url;
  });
}
export function FotoPage() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const [session] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('s') || '';
    } catch {
      return '';
    }
  });
  const [status, setStatus] = useState<Status>('idle');
  const [left, setLeft] = useState<number | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const mail = <a className="underline font-medium text-brand-textPrimary" href={`mailto:${CONTACT_EMAIL}`} data-id="element-520" data-mp="3ctp5t2ttrh6v">{CONTACT_EMAIL}</a>;
  const btn = 'block w-full text-center px-4 py-4 rounded-xl bg-brand-textPrimary text-white font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 disabled:opacity-50';
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setStatus('sending');
    setReasons([]);
    try {
      const photo = await toBase64(file);
      if (photo.length > 5900000) {
        setStatus('toobig');
        return;
      }
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON,
          Authorization: 'Bearer ' + ANON
        },
        body: JSON.stringify({
          session,
          photo,
          lang: language
        })
      });
      let data: any = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (typeof data.attemptsLeft === 'number') setLeft(data.attemptsLeft);
      if (Array.isArray(data.reasons)) setReasons(data.reasons.filter((r: unknown) => typeof r === 'string'));
      if (data.ok) setStatus('ok');else if (data.error === 'already_delivered') setStatus('done');else if (data.error === 'exhausted') setStatus('exhausted');else if (data.error === 'not_found' || res.status === 404) setStatus('notfound');else if (data.error === 'retry') setStatus('retry');else setStatus('error');
    } catch {
      setStatus('error');
    }
  }
  if (!session) {
    return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-521" data-mp="35g6z2nhuk3bp">
        <h1 className="text-3xl font-bold text-brand-textPrimary mb-4" data-id="element-522" data-mp="35ib3r7nkl6d1">{t.missingTitle}</h1>
        <p className="text-brand-textSecondary" data-id="element-523" data-mp="3jdluq9yp17c4">{t.missingBody} {mail}</p>
      </div>;
  }
  const canUpload = status === 'idle' || status === 'retry' || status === 'error' || status === 'toobig';
  const shownReasons = reasons.filter(r => t.reasons[r]);
  return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-524" data-mp="1l1etet9c8qwx">
      <h1 className="text-3xl sm:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-525" data-mp="1avn1zdw68xrg">{t.title}</h1>
      <p className="text-lg text-brand-textSecondary mb-6" data-id="element-526" data-mp="1ey9wxno52ysv">{t.intro}</p>

      {(status === 'idle' || status === 'retry' || status === 'error' || status === 'toobig' || status === 'sending') && <div className="rounded-xl border border-brand-border bg-white p-4 mb-6" data-id="element-527" data-mp="1656knip8crzt">
          <h2 className="font-semibold text-brand-textPrimary mb-2" data-id="element-528" data-mp="r85tbgayaz1v">{t.tipsTitle}</h2>
          <ul className="list-disc pl-5 text-brand-textSecondary space-y-1" data-id="element-529" data-mp="railemafapsn">
            {t.tips.map(x => <li key={x} data-id="element-530" data-mp="w562w170ejxq">{x}</li>)}
          </ul>
        </div>}

      {status === 'sending' && <div role="status" className="rounded-xl border border-brand-border bg-white p-4 mb-6" data-id="element-531" data-mp="i9u7rsoxyrga">
          <p className="font-semibold text-brand-textPrimary" data-id="element-532" data-mp="n8f7lr0a7947">{t.sending}</p>
          <p className="text-sm text-brand-textSecondary mt-1" data-id="element-533" data-mp="2cma8yf2vrfea">{t.waitNote}</p>
        </div>}

      {status === 'ok' && <div role="status" className="rounded-xl border border-green-300 bg-green-50 p-4 mb-6" data-id="element-534" data-mp="3q5148us3m8p3">
          <p className="font-semibold text-brand-textPrimary" data-id="element-535" data-mp="3l5xyf84anyg2">{t.okTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-536" data-mp="3l6ps2debzm3p">{t.okBody}</p>
        </div>}
      {status === 'done' && <div role="status" className="rounded-xl border border-green-300 bg-green-50 p-4 mb-6" data-id="element-537" data-mp="3cdm95pkx2h1o">
          <p className="font-semibold text-brand-textPrimary" data-id="element-538" data-mp="3cbt9wvcmuz3p">{t.doneTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-539" data-mp="37fufaq0pokuu">{t.doneBody}</p>
        </div>}
      {status === 'retry' && <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-6" data-id="element-540" data-mp="2xgdeon0bgx5q">
          <p className="font-semibold text-brand-textPrimary" data-id="element-541" data-mp="2tertmedmpckg">{t.retryTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-542" data-mp="2tguu5sceqo3f">{t.retryBody}</p>
          {shownReasons.length > 0 && <div className="mt-3" data-id="element-543" data-mp="vmnuxtnw0v0u">
              <p className="font-semibold text-brand-textPrimary" data-id="element-544" data-mp="rn5a0xw2gjy0">{t.reasonsTitle}</p>
              <ul className="list-disc pl-5 text-brand-textSecondary space-y-1 mt-1" data-id="element-545" data-mp="29ljs1ulom7e6">
                {shownReasons.map(r => <li key={r} data-id="element-546" data-mp="2ejbh8guxzfeo">{t.reasons[r]}</li>)}
              </ul>
            </div>}
          {left !== null && <p className="text-sm text-brand-textSecondary mt-3" data-id="element-547" data-mp="2jc4nj9626vsv">{t.left} {left}</p>}
        </div>}
      {status === 'exhausted' && <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-6" data-id="element-548" data-mp="2ne9fm3guzkde">
          <p className="font-semibold text-brand-textPrimary" data-id="element-549" data-mp="1qo8qu4ghp3w3">{t.exhaustedTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-550" data-mp="1vliq0btoktx4">{t.exhaustedBody}</p>
        </div>}
      {status === 'notfound' && <div role="alert" className="rounded-xl border border-red-300 bg-red-50 p-4 mb-6" data-id="element-551" data-mp="24lxf7m9rmt5w">
          <p className="text-brand-textPrimary" data-id="element-552" data-mp="3b212u23xthtg">{t.notFound} {mail}</p>
        </div>}
      {status === 'toobig' && <div role="alert" className="rounded-xl border border-red-300 bg-red-50 p-4 mb-6" data-id="element-553" data-mp="3g0ko00apipkr">
          <p className="text-brand-textPrimary" data-id="element-554" data-mp="3hi3cj8l1x19m">{t.tooBig}</p>
        </div>}
      {status === 'error' && <div role="alert" className="rounded-xl border border-red-300 bg-red-50 p-4 mb-6" data-id="element-555" data-mp="3mg4qxg7j8cer">
          <p className="text-brand-textPrimary" data-id="element-556" data-mp="3r73dgxj466qg">{t.generic} {mail}</p>
        </div>}

      {canUpload && <>
          {/* Sin atributo capture: el teléfono ofrece elegir entre cámara y galería. */}
          <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="sr-only" id="foto-input" data-id="element-557" data-mp="2yk0qn2nsvkg1" />
          <label htmlFor="foto-input" className={btn + ' cursor-pointer'} data-id="element-558" data-mp="33ikcl32e8w8e">{t.choose}</label>
        </>}

      <p className="text-sm text-brand-textSecondary mt-6" data-id="element-559" data-mp="38fu0ufe1r1un">{t.disclaimer}</p>
    </div>;
}